import React, { useState, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { Users, Download, Stethoscope, FolderX, FolderCheck, FileCheck2 } from 'lucide-react';
import Page from 'components/shared/Page';
import Pagination from 'components/shared/Pagination';
import ConfirmationModal from 'components/shared/ConfirmationModal';
import EmptyCard from 'components/shared/EmptyCard';
import CategoryBadge from 'components/shared/CategoryBadge';
import { useAPI, useDebounce, useGetUser, useGetPilgrim, useGetAgency } from 'hooks';
import { getUserName, formatNumber, createDownloadable, formatDate } from 'helpers';
import { PreferencesContext } from 'context';
import Locales from 'locales';
import {
  agencyCategories,
  FullPaymentIcon,
  PartialPaymentIcon,
  NpiCheckSuccessIcon,
  NpiCheckFailIcon,
  NpiCheckPendingIcon,
  CATEGORY_ENCADREUR_RELIGIEUX,
  CATEGORY_ENCADREUR_MEDICAL,
} from 'data';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Badge } from 'components/ui/badge';
import { Sheet } from 'components/ui/sheet';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import {
  AddTripModal,
  CreateGroupTripModal,
  EditTripModal,
  MedicalCheckupModal,
  InvoiceModal,
  PilgrimDetails,
  PilgrimDetailsTrigger,
  CategoriesModal,
  TransferAgencyModal,
} from './components';
import TripFilters, { getTripAppliedFiltersFromSearchParams } from './components/TripFilters';
import useAgencyTransfer from './useAgencyTransfer';
import { useAGLOCampaign, useCampaignSwitchStatus } from 'hooks/useAGLOCampaign';
import HistoricalCampaignStrip from 'components/shared/HistoricalCampaignStrip';

const ClampedTextWithTooltip = ({ value, className = '' }) => {
  if (!value) return <span className="text-[#89958d]">-</span>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className={`line-clamp-2 max-w-[220px] whitespace-normal break-words focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2c8155] ${className}`}
        >
          {value}
        </span>
      </TooltipTrigger>
      <TooltipContent portalled className="max-w-[280px] break-words">
        {value}
      </TooltipContent>
    </Tooltip>
  );
};

ClampedTextWithTooltip.propTypes = {
  value: PropTypes.string,
  className: PropTypes.string,
};

const isAgencySelectablePilgrim = (trip) => trip?.self_registered !== true;

const PilgrimsPage = () => {
  const { isAGLO, isAgency, isMAEC, isMAECPrimary, isDEI, isMS, isMSSecondary, currentUserEmail } = useGetUser();
  const { api } = useAPI();
  const {
    selectedCampaignYear,
    params: campaignParams,
    scope: campaignScope,
    isHistoricalCampaign,
    isCampaignSwitching,
    isReadOnly,
  } = useAGLOCampaign();
  const {
    registrationDeposit,
    religiousSupervisorsRatio,
    medicalSupervisorsRatio,
    pilgrimEligibilityMinAge,
    totalPilgrimQuota,
  } = useContext(PreferencesContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const preSetSearch = searchParams.get('q') || '';
  const urlQueryFilters = useMemo(
    () =>
      getTripAppliedFiltersFromSearchParams(searchParams, {
        isAgency,
        isAGLO,
        isDEI,
        isMAEC,
        isMS,
      }),
    [isAGLO, isAgency, isDEI, isMAEC, isMS, searchParams]
  );
  const [queryFilters, setQueryFilter] = useState(urlQueryFilters);
  const [search, setSearch] = useState(preSetSearch);
  const [cursor, setCursor] = useState('');
  const [cursorYear, setCursorYear] = useState(selectedCampaignYear);
  const effectiveCursor = isAGLO && cursorYear !== selectedCampaignYear ? '' : cursor;
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
  const [openMedicalModal, setOpenMedicalModal] = useState(false);
  const [openDeclineVisaModal, setOpenDeclineVisaModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openCreateGroupModal, setOpenCreateGroupModal] = useState(false);
  const [invoicePilgrims, setInvoicePilgrims] = useState([]);
  const [openCategoriesModal, setOpenCategoriesModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedPilgrims, setSelectedPilgrims] = useState([]);
  const debouncedSearchQuery = useDebounce(search, 1000);
  const queryClient = useQueryClient();
  const actionMenuTriggerRef = useRef(null);
  const transferAuthorizationScope = JSON.stringify([isAGLO, isReadOnly, ...campaignScope]);
  const updateSelectedTrip = useCallback((tripId, trip) => {
    setSelectedItem((current) => (current?.id === tripId ? trip : current));
  }, []);
  const limit = isMAEC ? 600 : 60;
  const effectiveSearchQuery = search ? debouncedSearchQuery : '';

  const fetchParams = useMemo(
    () => ({
      ...(queryFilters || {}),
      ...(effectiveSearchQuery ? { q: effectiveSearchQuery } : {}),
      ...(effectiveCursor ? { cursor: effectiveCursor } : {}),
      ...campaignParams,
      limit,
    }),
    [campaignParams.campaign_year, effectiveCursor, effectiveSearchQuery, limit, queryFilters]
  );
  const paginationKey = useMemo(
    () => JSON.stringify({ ...(queryFilters || {}), q: search || '', campaign: isAGLO ? selectedCampaignYear : null }),
    [isAGLO, queryFilters, search, selectedCampaignYear]
  );
  const isAgencyOrAGLO = isAgency || isAGLO;
  const showSocietyColumn = !isAgency;
  const tableMinWidth =
    400 +
    (showSocietyColumn ? 180 : 0) +
    180 +
    (isMAEC || isAgency ? 48 : 0) +
    (isMAEC ? 160 : 0) +
    (isMS ? 145 : 0) +
    (isAgency || isAGLO || isMS ? 110 : 0) +
    (isAgency || isAGLO || isMAEC ? 135 : 0) +
    (isAgencyOrAGLO ? 135 : isMS || isMAEC ? 75 : 0);

  const tripQuery = useQuery({
    queryFn: api.list('trip/', fetchParams),
    queryKey: ['trip/', fetchParams, ...campaignScope],
    keepPreviousData: true,
    enabled: !isDEI,
  });

  const { data: previousTrips, isFetching: isFetchingTrips } = tripQuery;
  const trips = isAGLO && isCampaignSwitching && tripQuery.isPreviousData ? undefined : previousTrips;

  const { data: categoriesData } = useQuery({
    queryFn: api.list('category/'),
    queryKey: ['category/'],
    enabled: !!trips,
  });

  const agenciesQuery = useQuery({
    queryFn: api.list('agency/', campaignParams),
    queryKey: ['agency/', campaignParams, ...campaignScope],
    enabled: !isDEI,
  });

  const { data: agencies } = agenciesQuery;
  const agencyTransfer = useAgencyTransfer({
    api,
    queryClient,
    agencies,
    isAuthorized: isAGLO && !isReadOnly,
    scopeKey: transferAuthorizationScope,
    onTripChange: updateSelectedTrip,
  });
  useCampaignSwitchStatus([tripQuery, agenciesQuery]);

  const { data: currentAgencyArray } = useQuery({
    queryFn: api.list('agency/', { agency: 'is_sa' }),
    queryKey: ['agency/', 'is_sa'],
    enabled: isAgency,
  });

  const { data: tripsStatsCategoryPaidData } = useQuery({
    queryFn: api.list('trip/stats/trip_category_paid/'),
    queryKey: ['trip/stats/trip_category_paid/'],
    enabled: isAgency || (isAGLO && !isHistoricalCampaign),
  });

  const currentAgency = currentAgencyArray ? currentAgencyArray[0] : null;

  const { mergerRequested, fullyPaidPilgrimCount } = useGetAgency(currentAgency);

  const tripsStatsCategoryPaid = tripsStatsCategoryPaidData?.data.data;

  const tripPaidCount = fullyPaidPilgrimCount || 0;

  // total pilgrims paid for all Agencies
  const totalPilgrimsPaid = categoriesData?.reduce((sum, c) => sum + (c.occupied_seats || 0), 0) || 0;

  const remainingPlaces = totalPilgrimQuota - totalPilgrimsPaid;

  const religiousSupervisorsCount =
    tripsStatsCategoryPaid?.find((stats) => stats.name.includes(CATEGORY_ENCADREUR_RELIGIEUX.toLocaleLowerCase()))
      ?.value || 0;

  const medicalSupervisorsCount =
    tripsStatsCategoryPaid?.find((stats) => stats.name.includes(CATEGORY_ENCADREUR_MEDICAL.toLocaleLowerCase()))
      ?.value || 0;

  const isCategoryDisplayed = (category) => {
    // Priority 1: Admin always sees all categories.
    if (!isAgency) return true;

    const isReligious = category.name === CATEGORY_ENCADREUR_RELIGIEUX;
    const isMedical = category.name === CATEGORY_ENCADREUR_MEDICAL;

    // 1. Non-Supervisor Categories Check
    if (!isReligious && !isMedical) {
      return currentAgency ? currentAgency.category_ids.includes(category.id) : false;
    }

    // === Supervisor Category Logic ===

    const supervisorsCount = isReligious ? religiousSupervisorsCount : medicalSupervisorsCount;
    const ratio = isReligious ? religiousSupervisorsRatio : medicalSupervisorsRatio;

    // Calculate the maximum allowed supervisors based on the ratio rule (Normal Case quota).
    const supervisorsAllowed = Math.floor((tripPaidCount - supervisorsCount) / ratio);

    // Check if the agency is currently under the normal ratio limit.
    const isUnderNormalLimit = supervisorsCount < supervisorsAllowed;

    // 2. Normal Case: ratio rules still allow adding supervisors.
    if (isUnderNormalLimit) return true;

    // If we reach this point, the agency is BLOCKED by the Normal Ratio Limit.

    // 3. Border Case Check: Is the global quota near its limit?
    const isBorderCase = remainingPlaces < ratio;

    if (!isBorderCase) {
      // Not in border case → respect the normal blocking rule.
      return false;
    }

    // === Border Case Activated ===
    // This overrides the normal ratio check (which failed above) to allow one last supervisor.

    // Find the agency with the most fully paid pilgrims.
    const topAgency =
      agencies && agencies.length > 0
        ? agencies.reduce(
            (max, a) => ((a.count_pilgrim_fully_paid || 0) > (max.count_pilgrim_fully_paid || 0) ? a : max),
            agencies[0]
          )
        : null;

    const isTopAgency = !!currentAgency && !!topAgency && currentAgency.id === topAgency.id;

    // If border case is active, ONLY the top agency may add.
    // The previous check (isUnderNormalLimit) is ignored here as per the special rule.
    return isTopAgency;
  };

  const categories = categoriesData
    ? categoriesData
        .filter((category) => agencyCategories.includes(category.name) && isCategoryDisplayed(category))
        .map((category) => ({
          ...category,
          value: category.id,
          label: category.name,
        }))
        .reverse()
    : [];

  const agencySelectablePilgrimIds = isAgency
    ? (trips || []).filter(isAgencySelectablePilgrim).map((trip) => trip.id)
    : [];
  const effectiveSelectedPilgrimIds = isAgency
    ? selectedPilgrims.filter((pilgrimId) => agencySelectablePilgrimIds.includes(pilgrimId))
    : selectedPilgrims;
  const selectedPilgrimsData = trips ? trips.filter((trip) => effectiveSelectedPilgrimIds.includes(trip.id)) : [];
  const areAllAgencyPilgrimsSelected =
    agencySelectablePilgrimIds.length > 0 &&
    agencySelectablePilgrimIds.every((pilgrimId) => effectiveSelectedPilgrimIds.includes(pilgrimId));

  const exportListParams = useMemo(
    () =>
      new URLSearchParams({
        ...(queryFilters || {}),
        ...(effectiveSearchQuery ? { q: effectiveSearchQuery } : {}),
        ...campaignParams,
      }).toString(),
    [campaignParams.campaign_year, effectiveSearchQuery, queryFilters]
  );

  useEffect(() => {
    setSearch(preSetSearch);
    setQueryFilter(urlQueryFilters);
  }, [preSetSearch, urlQueryFilters]);

  useEffect(() => {
    setCursor('');
    if (isAGLO && cursorYear !== selectedCampaignYear) {
      setCursorYear(selectedCampaignYear);
      setSelectedItem(null);
      setSelectedPilgrims([]);
      setOpenEditModal(false);
      setOpenDeleteModal(false);
      setOpenInvoiceModal(false);
      setOpenCreateGroupModal(false);
      agencyTransfer.reset();
    }
  }, [agencyTransfer.reset, cursorYear, isAGLO, paginationKey, selectedCampaignYear]);

  const handleEditPilgrim = (trip) => {
    if (isReadOnly) return;
    setSelectedItem(trip);
    setOpenEditModal(true);
  };

  const handleInvoice = (trip) => {
    setInvoicePilgrims([trip]);
    setOpenInvoiceModal(true);
  };

  const handleGenerateInvoices = () => {
    setInvoicePilgrims(selectedPilgrimsData);
    setOpenInvoiceModal(true);
  };

  const handleMedicalCheckup = (trip) => {
    setSelectedItem(trip);
    setOpenMedicalModal(true);
  };

  const handleDeclineVisa = (trip) => {
    setSelectedItem(trip);
    setOpenDeclineVisaModal(true);
  };

  const handleDelete = (trip) => {
    if (isReadOnly) return;
    setOpenDeleteModal(true);
    setSelectedItem(trip);
  };

  const handleCategories = () => setOpenCategoriesModal(true);

  const handleSelectDetails = (trip) => {
    if (!agencyTransfer.isPending) agencyTransfer.reset();
    setSelectedItem(trip);
  };

  const handleOpenTransfer = (trip) => {
    agencyTransfer.open(trip);
  };

  const handleCloseTransfer = () => {
    if (agencyTransfer.close()) setTimeout(() => actionMenuTriggerRef.current?.focus(), 0);
  };

  const unSelectAll = () => setSelectedPilgrims([]);

  const toggleSelectAll = () => {
    if (isAgency) {
      if (areAllAgencyPilgrimsSelected) {
        unSelectAll();
      } else {
        setSelectedPilgrims(agencySelectablePilgrimIds);
      }
      return;
    }

    if (effectiveSelectedPilgrimIds.length === trips.length) {
      unSelectAll();
    } else {
      setSelectedPilgrims(trips.map((trip) => trip.id));
    }
  };

  const toggleSelectPilgrim = (trip) => {
    if (isAgency && !isAgencySelectablePilgrim(trip)) return;

    setSelectedPilgrims((prev) =>
      prev.includes(trip.id) ? prev.filter((pilgrimId) => pilgrimId !== trip.id) : [...prev, trip.id]
    );
  };

  const closeCreateGroupModal = () => {
    setOpenCreateGroupModal(false);
    unSelectAll();
  };

  const { mutate: approveVisaSubmission } = useMutation({
    mutationFn: (trips) => api.post('trip/visa_submission_approve/', trips),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] });
      toast.success(Locales.trip.toast.visa_request_approved);
      unSelectAll();
    },
  });

  const { mutate: declineVisaSubmission } = useMutation({
    mutationFn: (id) => api.post(`trip/${id}/visa_submission_cancel/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] });
      toast.success(Locales.trip.toast.visa_request_declined);
    },
  });

  const { mutate: deleteTrip } = useMutation({
    meta: { agloCampaignWrite: isAGLO },
    mutationFn: (id) => api.remove('trip', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/category/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/trip_category_paid/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/groupes/'] });
      toast.success(Locales.trip.toast.delete);
    },
  });

  const { mutate: generateMedicalCertificate } = useMutation({
    mutationFn: ({ id }) => api.get(`trip/${id}/generate_medical_certificate/`, { responseType: 'blob' }),
    onSuccess: ({ data }, { pilgrimData }) => {
      const fileName = `certificat_medicale_de_${getUserName(
        pilgrimData?.pilgrim?.first_name,
        pilgrimData?.pilgrim?.last_name
      )}_${pilgrimData?.pilgrim?.passport || pilgrimData?.pilgrim?.npi}.pdf`;

      createDownloadable(data, fileName, 'application/pdf');
      toast.success('Téléchargement réussi');
    },
  });

  const { mutate: exportPilgrimList } = useMutation({
    mutationFn: ({ requestParams }) => api.get(`trip/export/csv/?${requestParams}`),
    onSuccess: ({ data }, { filename }) => {
      createDownloadable(data, filename, 'csv');
      toast.success('Téléchargement réussi');
    },
  });

  const handleGenerateMedicalCertificate = (pilgrimData) => {
    if (isReadOnly) return;
    generateMedicalCertificate({ id: pilgrimData.id, pilgrimData });
  };

  const handlePrintPDF = () => {
    if (selectedPilgrimsData.length === 0) {
      toast.error('Veuillez sélectionner au moins un pèlerin à imprimer.');
      return;
    }
    // 1. Initialize document in Landscape mode
    const doc = new jsPDF('l', 'mm', 'a4');
    const dateStr = format(new Date(), 'dd/MM/yyyy à HH:mm');

    // 2. Add Title and Metadata
    doc.setFontSize(18);
    doc.text('Liste des Pèlerins', 14, 15);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Nombre d'éléments : ${selectedPilgrimsData.length} | Généré le : ${dateStr}`, 14, 22);

    // 3. Map Data (Excluding UI elements like Checkboxes and Buttons)
    // We use the same logic as your table rows to ensure data consistency
    const tableData = selectedPilgrimsData.map((trip) => {
      // Note: Adjust these keys based on your useGetPilgrim or trip object structure
      const {
        npi,
        firstname,
        lastname,
        passport,
        passportExpirationDate,
        categoryName,
        agencyName,
        isApprovedForMAECStep,
      } = useGetPilgrim(trip);
      return [
        npi || '-',
        lastname?.toUpperCase() || '-',
        firstname || '-',
        passport || '-',
        (passportExpirationDate && formatDate(passportExpirationDate)) || '-',
        categoryName || '-',
        agencyName || '-',
        isApprovedForMAECStep ? 'Déposé' : 'En attente',
      ];
    });

    // 4. Define Headers
    const tableHeaders = [
      [
        'NPI',
        'Nom',
        'Prénoms',
        'Passeport',
        "Date d'expiration du passeport",
        'Catégorie',
        'Société',
        'Statut Passport',
      ],
    ];

    // 5. Generate Table
    autoTable(doc, {
      head: tableHeaders,
      body: tableData,
      startY: 28,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [0, 102, 51], // Hajj Green
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: 30 }, // NPI
        3: { halign: 'center' }, // Passport
        6: { halign: 'center' }, // Status
      },
      // Optional: Alternating row colors
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      margin: { top: 30 },
    });

    // 6. Download
    const fileName = `liste_pelerins_${format(new Date(), 'dd_MM_yyyy_HHmm')}.pdf`;
    doc.save(fileName);
  };

  return (
    <Page
      title="Liste des Pèlerins"
      headerContent={<HistoricalCampaignStrip />}
      actionDisabled={isReadOnly}
      description="Gérez les informations essentielles des pèlerins"
      actionLabel={`${isAgency && !mergerRequested ? '+ Ajouter un pèlerin' : ''}`}
      onAction={() => setOpenAddModal(true)}
      bodyClassName="[&>div:empty]:hidden"
      removeContentStyles
      onPrint={
        trips && trips.length > 0
          ? () =>
              exportPilgrimList({
                filename: isAGLO ? `Liste des pèlerins_${selectedCampaignYear}.csv` : 'Liste des pèlerins',
                requestParams: exportListParams,
              })
          : undefined
      }
      printTooltip="Télécharger la liste des pèlerins"
    >
      {!trips && <EmptyCard />}

      {trips && (
        <>
          <Sheet>
            <div className="overflow-hidden rounded-[10px] border border-[#dfe5e1] bg-white shadow-none">
              <TripFilters
                agencies={
                  isAGLO && queryFilters?.agency && agencies && !agencies.some(({ id }) => id === queryFilters.agency)
                    ? [
                        { id: queryFilters.agency, name: `Société ${queryFilters.agency} (absente de cette campagne)` },
                        ...agencies,
                      ]
                    : agencies
                }
                trips={trips}
                categories={categories}
                queryFilters={queryFilters}
                setQueryFilter={setQueryFilter}
                setSearchParams={setSearchParams}
                search={search}
                setSearch={setSearch}
                isFetchingTrips={isFetchingTrips}
              />

              {effectiveSelectedPilgrimIds.length > 0 && (
                <div className="mx-4 mb-3 flex flex-wrap items-center gap-3 rounded-md border border-[#dbe9df] bg-[#f4f9f5] px-3 py-2 sm:mx-5">
                  <p className="font-semibold text-[#315c43]">
                    {effectiveSelectedPilgrimIds.length} sélectionné{effectiveSelectedPilgrimIds.length > 1 ? 's' : ''}
                  </p>
                  <div className="ml-auto flex flex-wrap gap-2">
                    {isMAEC && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-[#d7dfda] bg-white shadow-none"
                          onClick={handlePrintPDF}
                        >
                          <Download />
                          Imprimer la liste
                        </Button>

                        <Button
                          variant="hajj"
                          size="sm"
                          className="shadow-none"
                          onClick={() => approveVisaSubmission(effectiveSelectedPilgrimIds)}
                        >
                          <FolderCheck />
                          {Locales.base.button.receive}
                        </Button>
                      </>
                    )}

                    {isAgency && (
                      <>
                        {effectiveSelectedPilgrimIds.length >= 2 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-[#d7dfda] bg-white shadow-none"
                            onClick={() => setOpenCreateGroupModal(true)}
                          >
                            <Users />
                            Créer un groupement
                          </Button>
                        )}
                        <Button variant="hajj" size="sm" className="shadow-none" onClick={handleGenerateInvoices}>
                          Générer une facture
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}

              <Table
                className="table-fixed text-sm text-[#26352c]"
                style={{ minWidth: tableMinWidth }}
                containerClassName="border-t border-[#e1e7e3]"
                containerProps={{
                  tabIndex: 0,
                  role: 'region',
                  'aria-label': 'Tableau des pèlerins, défilement horizontal',
                }}
              >
                <TableHeader className="bg-[#f6f8f7]">
                  <TableRow className="hover:bg-[#f6f8f7]">
                    {(isMAEC || isAgency) && (
                      <TableHead className="w-[48px] px-4 py-0">
                        <Checkbox
                          aria-label="Tout sélectionner"
                          checked={
                            isAgency
                              ? areAllAgencyPilgrimsSelected
                              : effectiveSelectedPilgrimIds.length === trips?.length
                          }
                          onCheckedChange={toggleSelectAll}
                          disabled={isAgency && agencySelectablePilgrimIds.length === 0}
                        />
                      </TableHead>
                    )}

                    <TableHead className="w-[400px]">Pèlerin</TableHead>
                    {showSocietyColumn && <TableHead className="w-[180px]">Société</TableHead>}
                    {isMAEC && <TableHead className="w-[160px]">Date d&apos;expiration</TableHead>}
                    <TableHead className="w-[180px]">Catégorie</TableHead>

                    {isMS && <TableHead className="w-[145px]">Médecin</TableHead>}

                    {(isAgency || isAGLO || isMS) && <TableHead className="w-[110px]">Santé</TableHead>}

                    {(isAgency || isAGLO || isMAEC) && <TableHead className="w-[135px]">Statut Passport</TableHead>}

                    {(isAgency || isAGLO) && <TableHead className="w-[135px] text-right">Montant payé</TableHead>}
                    {(isMS || isMAEC) && <TableHead className="w-[75px] text-center">Actions</TableHead>}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {trips.map((trip) => {
                    const {
                      id,
                      firstname,
                      lastname,
                      npi,
                      isNpiValid,
                      passport,
                      passportExpirationDate,
                      categoryName,
                      agencyName,
                      isHealthy,
                      depositTransferred,
                      selfRegistered,
                      isInvoiceFullyPaid,
                      isPartiallyPaid,
                      isGrouped,
                      groupName,
                      isApprovedForMAECStep,
                      isApprovedForMSStep,
                      doctorEmail,
                      doctorName,
                    } = useGetPilgrim(trip);

                    const disableMedicalCheckup =
                      isMSSecondary && isApprovedForMSStep !== null && currentUserEmail !== doctorEmail;
                    const isSelected = effectiveSelectedPilgrimIds.includes(id);

                    return (
                      <TableRow
                        key={id}
                        className={`border-[#edf0ee] hover:bg-[#fafcfb] ${
                          isSelected
                            ? 'bg-[#f0f7f2] hover:bg-[#eaf3ed]'
                            : isMAEC && isApprovedForMAECStep
                            ? 'bg-[#f7f9f8]'
                            : 'bg-white'
                        }`}
                      >
                        {(isMAEC || isAgency) && (
                          <TableCell className="w-[48px] px-4 py-2">
                            <Checkbox
                              aria-label={`Sélectionner ${firstname || ''} ${lastname || ''}`.trim()}
                              checked={isSelected}
                              onCheckedChange={() => toggleSelectPilgrim(trip)}
                              disabled={
                                (isMAEC && isApprovedForMAECStep) || (isAgency && !isAgencySelectablePilgrim(trip))
                              }
                            />
                          </TableCell>
                        )}

                        <TableCell className="whitespace-normal px-3 py-2.5 align-middle">
                          <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] items-start gap-x-[18px]">
                            <div className="min-w-0">
                              <dt className="text-xs font-normal leading-[18px] text-[#6a7770]">Nom</dt>
                              <dd className="break-words text-sm font-semibold leading-[19px] text-[#24372c]">
                                {lastname || '-'}
                              </dd>
                            </div>
                            <div className="min-w-0">
                              <dt className="text-xs font-normal leading-[18px] text-[#6a7770]">Prénoms</dt>
                              <dd className="break-words text-sm font-medium leading-[19px] text-[#24372c]">
                                {firstname || '-'}
                              </dd>
                            </div>
                            <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs text-[#68766d]">
                              <dt className="shrink-0">NPI</dt>
                              <dd className="flex min-w-0 items-center gap-1.5">
                                <strong className="break-all font-semibold tabular-nums tracking-[-0.02em] text-[#24372c]">
                                  {npi || '-'}
                                </strong>
                                {isNpiValid && <NpiCheckSuccessIcon appearance="compact" portalled />}
                                {isNpiValid === false && <NpiCheckFailIcon appearance="compact" portalled />}
                                {isNpiValid === null && <NpiCheckPendingIcon appearance="compact" portalled />}
                              </dd>
                            </div>
                            <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs text-[#68766d]">
                              <dt className="shrink-0">Passeport</dt>
                              <dd className="min-w-0">
                                {passport ? (
                                  <strong className="break-all font-semibold tabular-nums text-[#24372c]">
                                    {passport}
                                  </strong>
                                ) : (
                                  <span className="text-[#89958d]">Non renseigné</span>
                                )}
                              </dd>
                            </div>
                          </dl>

                          {isAgencyOrAGLO && (selfRegistered || isGrouped) && (
                            <div
                              className={`mt-0.5 grid min-w-0 items-start gap-x-4 text-xs leading-[18px] text-[#68766d] ${
                                selfRegistered && isGrouped ? 'grid-cols-2' : 'grid-cols-1'
                              }`}
                            >
                              {selfRegistered && <span className="whitespace-nowrap">Auto-inscrit</span>}
                              {isGrouped && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span
                                      tabIndex={0}
                                      className="min-w-0 truncate rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hajj-primary"
                                    >
                                      Groupement : {groupName}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent portalled className="max-w-[280px] break-words">
                                    Groupement : {groupName}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          )}

                          {isAgencyOrAGLO && (
                            <PilgrimDetailsTrigger pilgrimData={trip} onSelect={handleSelectDetails} />
                          )}
                        </TableCell>

                        {showSocietyColumn && (
                          <TableCell className="whitespace-normal px-3 py-2">
                            {agencyName ? (
                              <span className="break-words font-medium text-[#26352c]">{agencyName}</span>
                            ) : (
                              <span className="text-[#89958d]">-</span>
                            )}
                          </TableCell>
                        )}

                        {isMAEC && (
                          <TableCell className="px-3 py-2 tabular-nums">
                            {passportExpirationDate ? formatDate(passportExpirationDate) : '-'}
                          </TableCell>
                        )}

                        <TableCell className="px-3 py-2">
                          <CategoryBadge categoryName={categoryName} recoverable portalled />
                        </TableCell>

                        {isMS && (
                          <TableCell className="px-3 py-2">
                            <ClampedTextWithTooltip value={doctorName} className="max-w-[145px]" />
                          </TableCell>
                        )}

                        {(isAgency || isAGLO || isMS) && (
                          <TableCell className="px-3 py-2">
                            {isHealthy === null && <Badge variant="pending">En attente</Badge>}

                            {isHealthy !== null && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Badge
                                      variant={isHealthy ? 'success' : 'destructive'}
                                      className={isReadOnly ? 'gap-1' : 'cursor-pointer gap-1'}
                                      onClick={isReadOnly ? undefined : () => handleGenerateMedicalCertificate(trip)}
                                    >
                                      {!isReadOnly && <Download size={15} />}
                                      {isHealthy ? 'Apte' : 'Inapte'}
                                    </Badge>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent portalled>
                                  <p>{isReadOnly ? 'Visite médicale' : 'Télécharger votre certificat médicale'}</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </TableCell>
                        )}

                        {(isAgency || isAGLO || isMAEC) && (
                          <TableCell className="px-3 py-2">
                            {isApprovedForMAECStep && (
                              <Badge variant="success" className="gap-1">
                                <FileCheck2 size={15} /> Déposé
                              </Badge>
                            )}

                            {!isApprovedForMAECStep && <Badge variant="pending">En attente</Badge>}
                          </TableCell>
                        )}

                        {(isAgency || isAGLO) && (
                          <TableCell className="px-3 py-2 text-right align-middle tabular-nums">
                            <div className="flex items-center justify-end gap-1.5 whitespace-nowrap font-semibold">
                              <span>{formatNumber(depositTransferred)}</span>

                              {isInvoiceFullyPaid && <FullPaymentIcon appearance="compact" portalled />}

                              {isPartiallyPaid && <PartialPaymentIcon appearance="compact" portalled />}
                            </div>
                          </TableCell>
                        )}

                        {(isMS || isMAEC) && (
                          <TableCell className="px-3 py-2 text-center">
                            <div className="flex justify-center">
                              {isMS && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="hajj"
                                      size="icon"
                                      className="size-8 rounded-md shadow-none"
                                      aria-label={`Visite médicale de ${firstname || ''} ${lastname || ''}`.trim()}
                                      disabled={disableMedicalCheckup}
                                      onClick={() => handleMedicalCheckup(trip)}
                                    >
                                      <Stethoscope />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent portalled>
                                    <p> {`Visite médicale ${disableMedicalCheckup ? 'effectuée' : ''}`}</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}

                              {isMAEC && (
                                <>
                                  {isMAECPrimary && isApprovedForMAECStep && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="destructive"
                                          size="icon"
                                          className="size-8 rounded-md border border-[#f1ccd3] bg-[#fff1f3] text-[#ae3542] shadow-none hover:bg-[#ffe3e8]"
                                          aria-label={`Annuler la réception du passeport de ${firstname || ''} ${
                                            lastname || ''
                                          }`.trim()}
                                          onClick={() => handleDeclineVisa(trip)}
                                        >
                                          <FolderX />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent portalled>
                                        <p>Annuler la réception du passport</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}

                                  {!isApprovedForMAECStep && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="hajj"
                                          size="icon"
                                          className="size-8 rounded-md shadow-none"
                                          aria-label={`Réceptionner le passeport de ${firstname || ''} ${
                                            lastname || ''
                                          }`.trim()}
                                          onClick={() => approveVisaSubmission([trip.id])}
                                        >
                                          <FolderCheck />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent portalled>
                                        <p>Réceptionner le passport</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}

                  {trips.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={100} className="h-28 text-center font-medium text-[#657269]">
                        Aucune donnée disponible
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <Pagination
                key={paginationKey}
                data={trips}
                limit={limit}
                isFetching={isFetchingTrips}
                setCursor={setCursor}
              />
            </div>
            {selectedItem && (
              <>
                <PilgrimDetails
                  pilgrimData={selectedItem}
                  isReadOnly={isReadOnly}
                  pilgrimsList={trips}
                  onOpenEditModal={handleEditPilgrim}
                  onOpenInvoiceModal={handleInvoice}
                  onOpenDeleteModal={handleDelete}
                  onOpenTransferModal={handleOpenTransfer}
                  actionMenuTriggerRef={actionMenuTriggerRef}
                  isTransferPending={agencyTransfer.isPending}
                  handleGenerateMedicalCertificate={handleGenerateMedicalCertificate}
                />

                {agencyTransfer.isOpen && (
                  <TransferAgencyModal
                    key={agencyTransfer.session}
                    isOpened
                    pilgrimData={agencyTransfer.pilgrim}
                    agencies={agencies}
                    isAgenciesLoading={agenciesQuery.isFetching}
                    agencyError={agenciesQuery.isError}
                    isSubmitting={agencyTransfer.isSubmitting}
                    isReconciling={agencyTransfer.isReconciling}
                    isRetryBlocked={agencyTransfer.isRetryBlocked}
                    transferError={agencyTransfer.transferError}
                    onRetryAgencies={() => agenciesQuery.refetch()}
                    onRetryVerification={agencyTransfer.retryVerification}
                    onSubmit={agencyTransfer.submit}
                    onClose={handleCloseTransfer}
                  />
                )}

                {openEditModal && (
                  <EditTripModal
                    isOpened={openEditModal}
                    isReadOnly={isReadOnly}
                    trackCampaignWrite={isAGLO}
                    pilgrimData={selectedItem}
                    pilgrimEligibilityMinAge={pilgrimEligibilityMinAge}
                    categories={categories}
                    registrationDeposit={registrationDeposit}
                    openCategoriesModal={handleCategories}
                    closeModal={() => setOpenEditModal(false)}
                  />
                )}

                {openMedicalModal && (
                  <MedicalCheckupModal
                    isOpened={openMedicalModal}
                    pilgrimData={selectedItem}
                    closeModal={() => setOpenMedicalModal(false)}
                  />
                )}

                <ConfirmationModal
                  isOpen={openDeclineVisaModal}
                  setOpenModal={setOpenDeclineVisaModal}
                  title={Locales.trip.decline_visa_modal_title}
                  confirmBtnVariant="destructive"
                  confirmBtnLabel={Locales.trip.button.cancel_reception}
                  message={
                    <p>
                      {Locales.formatString(Locales.trip.decline_visa_modal_text, {
                        passport: <strong>{selectedItem.pilgrim.passport}</strong>,
                        fullname: (
                          <strong>
                            {getUserName(selectedItem.pilgrim.first_name, selectedItem.pilgrim.last_name)}
                          </strong>
                        ),
                      })}
                    </p>
                  }
                  onConfirm={() => declineVisaSubmission(selectedItem.id)}
                />

                <ConfirmationModal
                  isOpen={openDeleteModal}
                  setOpenModal={setOpenDeleteModal}
                  title={Locales.trip.modal.title.delete_trip}
                  confirmBtnVariant="destructive"
                  confirmBtnLabel={Locales.base.button.delete}
                  message={
                    <p>
                      {Locales.formatString(Locales.trip.modal.text.delete_trip, {
                        fullname: (
                          <strong>
                            {getUserName(selectedItem.pilgrim.first_name, selectedItem.pilgrim.last_name)}
                          </strong>
                        ),
                      })}
                    </p>
                  }
                  onConfirm={() => !isReadOnly && deleteTrip(selectedItem.id)}
                />
              </>
            )}
          </Sheet>
        </>
      )}

      {openAddModal && (
        <AddTripModal
          trips={trips}
          pilgrimEligibilityMinAge={pilgrimEligibilityMinAge}
          isOpened={openAddModal}
          categories={categories}
          openCategoriesModal={handleCategories}
          closeModal={() => setOpenAddModal(false)}
        />
      )}

      {openCreateGroupModal && (
        <CreateGroupTripModal
          isOpened={openCreateGroupModal}
          selectedPilgrimIds={effectiveSelectedPilgrimIds}
          selectedPilgrimsData={selectedPilgrimsData}
          closeModal={closeCreateGroupModal}
        />
      )}

      {openInvoiceModal && (
        <InvoiceModal
          isOpened={openInvoiceModal}
          selectedPilgrimsData={invoicePilgrims}
          advanceDeposit={registrationDeposit}
          closeModal={() => setOpenInvoiceModal(false)}
        />
      )}

      <CategoriesModal
        isOpened={openCategoriesModal}
        categories={categories}
        closeModal={() => setOpenCategoriesModal(false)}
      />
    </Page>
  );
};

export default PilgrimsPage;
